import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  usePassengerCountString,
  useFlightDatesString,
  type PassengerCounts,
} from "@apac/hooks";
import type { Airport } from "@b2bportal/air-shopping-api";
import {
  getAirports,
  getDestination,
  getOrigin,
} from "@b2bportal/core-flights";
import type { ITripTerminus } from "@b2bportal/core-types";
import type {
  DateTimeFormatStyle,
  Intl_ListFormatOptions,
} from "@hopper-b2b/i18n";

export type LocationDetails = {
  title: string;
  subtitle: string;
};

export type FlightLoadingDetails = {
  originDetails: LocationDetails;
  destinationDetails: LocationDetails;
  tripDate: string;
  passengerCounts: string;
};

interface UseFlightShopTitleResponse {
  context: FlightLoadingDetails;
  handlers: {
    onCancelClick: () => void;
  };
}

const getLocationDetails = (
  airportMap: Record<string, Airport> = {},
  location: ITripTerminus
): LocationDetails => {
  if (!location || !location.id || !location.id.code) {
    return { title: "", subtitle: "" };
  }
  const {
    code: { code: airportCode },
  } = location.id;

  const { cityName = "", geography } = airportMap?.[airportCode] || {};
  const { countryCode = "" } = geography || {};

  const subtitle =
    cityName && countryCode ? `${cityName} • ${countryCode}` : "";

  return {
    title: location?.label || "",
    subtitle,
  };
};

export type UseFlightLoadingDetailsParams = {
  dateStringSeparator?: string;
  dateFormatStyle?: DateTimeFormatStyle;
  passengerListFormat?: Intl_ListFormatOptions;
  passengerCounts?: PassengerCounts;
};

export const useFlightLoadingDetails = (
  params?: UseFlightLoadingDetailsParams
): UseFlightShopTitleResponse => {
  const tripDate = useFlightDatesString(
    params?.dateFormatStyle,
    params?.dateStringSeparator
  );
  const passengerCounts = usePassengerCountString(
    params?.passengerListFormat,
    params?.passengerCounts
  );
  const navigate = useNavigate();

  const origin = useSelector(getOrigin);
  const destination = useSelector(getDestination);
  const airportMap = useSelector(getAirports);

  const onCancelClick = () => {
    navigate(-1);
  };

  return {
    context: {
      tripDate,
      passengerCounts,
      originDetails: getLocationDetails(airportMap, origin),
      destinationDetails: getLocationDetails(airportMap, destination),
    },
    handlers: {
      onCancelClick,
    },
  };
};
